import { useBusyAction } from '@/db/useBusyAction';
import { currentUserIdState } from '@/state';
import logger from '@/util/logger';
import type { RecallJobId, RecallJobRequest } from '@rock/types';
import { UserIdSchema } from '@rock/types';
import { useAtomValue } from 'jotai';
import { toast } from 'sonner';
import { requestRecallJob } from './recallJobs';

const log = logger('useRecallJobRequest');

/** Request a recall job with a success toast; `canRequest` is false until someone is signed in. */
export function useRecallJobRequest() {
    const userId = useAtomValue(currentUserIdState);
    const { busy, run } = useBusyAction(log);

    const request = async (
        jobRequest: RecallJobRequest,
        label: string,
        describe: (jobId: RecallJobId) => string,
    ): Promise<void> => {
        if (!userId) return;
        await run('request recall job', async () => {
            const jobId = await requestRecallJob(jobRequest, UserIdSchema.parse(userId));
            log.info('Requested recall job', { jobId, jobRequest });
            toast.success(`${label} requested`, { description: describe(jobId) });
        });
    };

    return { request, isRequesting: busy, canRequest: !!userId };
}
